/* am7-reviews.js: the borrowed Judge.me widget in sections/am7-reviews.liquid.
   - Shows the first N cards (data-am7-count-m < 750px, data-am7-count-d from 750px); "SHOW MORE" reveals N more.
   - When every loaded card is showing, it asks Judge.me for its next page through Judge.me's own paging handler
     (a hidden .jdgm-paginate__load-more link: Judge.me fetches the page without moving focus; the wrapper's
     data-not-scroll-to stops its scrolling). Judge.me's standard paging REPLACES the list, so the cards already shown
     are put back in front of the new ones: the list only grows.
   - If fewer cards than N are loaded (Judge.me pages hold 5), the next page is fetched once the section is near.
   - Masonry: each card spans <its height + gap> rows of 1px (CSS: .am7-is-masonry), kept current by a ResizeObserver.
   - Larger review photos: Judge.me's thumbnails are 160px wide; a srcset asks its image server for the card width.
     Each photo also gets its src and is un-hidden once loaded, without waiting for Judge.me's own lazy loader.
   - Accessibility pass on Judge.me's cards (WCAG 2.2 AA, prepare()): the list and cards get list roles; the star rating
     loses Judge.me's tabindex="0" (a Tab stop that does nothing); each card's nodes are moved, inside the card, into
     the order shown on screen; "Item type:" + product is real text (not CSS content); photo links get unique names.
   - If SHOW MORE has to disappear while it has focus, focus moves to the last review shown and the reason is announced.
   Judge.me's nodes only get classes, roles/labels, inline grid spans, image attributes, our own child nodes and
   moves inside their card. Loaded with defer. */
(function () {
  'use strict';
  var AM7 = (window.AM7 = window.AM7 || {});
  if (AM7.__reviews) return;
  AM7.__reviews = true;

  var MQ_PHONE = '(max-width: 749.98px)';
  var LOAD_TIMEOUT = 12000;
  var MSG_FAILED = 'More reviews could not be loaded.';
  var MSG_ALL = 'All reviews are showing.';
  var MSG_LATE = 'More reviews have loaded.';

  function arr(list) { return Array.prototype.slice.call(list || []); }
  function reviewId(el) { return el.getAttribute('data-review-id') || ''; }
  function text(el) { return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }
  function uid(prefix) { return prefix + Math.random().toString(36).slice(2, 9); }
  function follows(a, b) { return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING); } // b comes after a

  function Reviews(root) {
    var self = this;
    this.root = root;
    this.widget = root.querySelector('[data-am7-reviews-widget]');
    this.btn = root.querySelector('[data-am7-reviews-more]');
    this.status = root.querySelector('[data-am7-reviews-status]');
    this.countM = parseInt(root.getAttribute('data-am7-count-m'), 10) || 4;
    this.countD = parseInt(root.getAttribute('data-am7-count-d'), 10) || 6;
    var itemLabel = root.getAttribute('data-am7-item-label');   // absent (an older section file): the default label
    this.itemLabel = itemLabel === null ? 'Item type:' : itemLabel.trim();
    this.mq = window.matchMedia ? window.matchMedia(MQ_PHONE) : { matches: false };
    this.shown = null;     // null = follow the breakpoint count; a number once the visitor asked for more
    this.loading = null;   // { keep: [cards], before: n, silent: bool, timer }
    this.late = null;      // a request that timed out: if Judge.me still answers, its page is merged like a normal one
    this.failed = false;   // Judge.me's paging did not answer: stop offering more pages
    this.done = false;     // a page the visitor asked for brought no new reviews: stop offering more pages
    this.near = false;     // section within ~1.5 screens of the viewport
    if (!this.widget) return;

    root.classList.add('am7-is-js');

    if (this.btn) this.btn.addEventListener('click', function () { self.more(); });
    if (this.mq.addEventListener) this.mq.addEventListener('change', function () { self.sync(); });
    else if (this.mq.addListener) this.mq.addListener(function () { self.sync(); });

    this.ro = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.target.classList.contains('jdgm-rev')) self.readMore([en.target]);
        self.span(en.target);
      });
    }) : null;
    if (this.ro) {
      // the widget appears (Judge.me's CSS/script ready) or changes width: refresh the button and the spans
      var lastKey = '';
      new ResizeObserver(function (entries) {
        var r = entries[0].contentRect, key = Math.round(r.width) + ':' + (r.height > 0);
        if (key !== lastKey) { lastKey = key; self.sync(); }
      }).observe(this.widget);
    } else {
      window.addEventListener('resize', function () { self.layout(); });
    }

    // Judge.me sets the widget up, and (re)renders pages, after this script runs: follow its DOM changes.
    new MutationObserver(function () { self.onMutate(); }).observe(this.widget, { childList: true, subtree: true });

    // Judge.me's paging replaces the whole list, and the browser drops focus from the nodes it takes out, even though
    // onMutate() puts the cards back. Track focus inside the widget so it can be put back (WCAG 2.4.3).
    // at: 0 = still focused (or removed without an event), -1 = moved to another element, else the time it was blurred.
    this.focus = null;
    this.widget.addEventListener('focusin', function (e) { self.focus = { el: e.target, at: 0 }; });
    this.widget.addEventListener('focusout', function (e) {
      if (self.focus && self.focus.el === e.target) self.focus.at = e.relatedTarget ? -1 : Date.now();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.near = entries[entries.length - 1].isIntersecting;
        if (self.near) self.sync();
      }, { rootMargin: '150% 0px 150% 0px' }).observe(root);
    } else {
      this.near = true;
    }
    this.sync();
  }

  Reviews.prototype.step = function () { return this.mq.matches ? this.countM : this.countD; };
  Reviews.prototype.limit = function () { return this.shown === null ? this.step() : this.shown; };
  Reviews.prototype.list = function () { return this.widget.querySelector('.jdgm-rev-widg__reviews'); };
  Reviews.prototype.cards = function () {
    var list = this.list();
    return list ? arr(list.children).filter(function (el) { return el.classList.contains('jdgm-rev'); }) : [];
  };
  Reviews.prototype.nextLink = function () {
    return this.widget.querySelector('.jdgm-paginate .jdgm-paginate__next-page[data-page]');
  };
  // Judge.me keeps the whole widget hidden (display: none) until its script has set it up.
  Reviews.prototype.visible = function () {
    var w = this.widget.querySelector('.jdgm-rev-widg');
    return !!(w && w.getClientRects().length);
  };

  /* ---------- state -> DOM ---------- */
  Reviews.prototype.sync = function () {
    var cards = this.cards(), limit = this.limit();
    cards.forEach(function (c, i) { c.classList.toggle('am7-reviews__off', i >= limit); });
    this.prepare(cards);
    this.upgradePhotos(cards);
    this.dedupeTitles(cards);
    this.readMore(cards);
    this.layout();
    var canFetch = !!this.nextLink() && !this.failed && !this.done && this.visible();
    // fill the first view (Judge.me pages hold 5 cards; the design shows 6)
    if (!this.loading && this.shown === null && canFetch && this.near && cards.length < limit) this.load(true);
    if (this.btn) {
      var more = this.visible() && (cards.length > limit || canFetch);
      // the button is about to disappear while it has focus: keep the visitor's place (reveal() handles its own case)
      if (!more && !this.btn.hidden && !this.revealing && document.activeElement === this.btn) this.rescue();
      this.btn.hidden = !more;
      this.btn.setAttribute('aria-busy', this.loading && !this.loading.silent ? 'true' : 'false');
    }
  };

  /* ---------- accessibility pass over Judge.me's cards (every sync; each write only when something changes) ---------- */
  Reviews.prototype.prepare = function (cards) {
    var list = this.list(), self = this, names = {};
    // a list of reviews: screen readers announce the count and where each review starts and ends (WCAG 1.3.1)
    if (list && list.getAttribute('role') !== 'list') list.setAttribute('role', 'list');
    cards.forEach(function (c, i) {
      if (c.getAttribute('role') !== 'listitem') c.setAttribute('role', 'listitem');
      // Judge.me makes each star rating a Tab stop (tabindex="0" on a role="img" that does nothing). Out of the Tab order;
      // role and aria-label stay, so the rating is still read (WCAG 2.4.3)
      arr(c.querySelectorAll('.jdgm-rev__rating[tabindex]')).forEach(function (r) { r.removeAttribute('tabindex'); });
      self.reorder(c);   // every pass: Judge.me rebuilds the card header once more after its set-up
      self.addItem(c);
      self.namePhotos(c, i, names);
    });
  };

  // DOM order = the order on screen (photo, name, date, stars, title, text, product), so Tab and screen-reader order
  // follow what is seen (WCAG 1.3.2 / 2.4.3). Every move stays inside the card and only happens when a node is out of
  // order (so it settles: a move is a mutation, and mutations call sync() again); the CSS order values are unchanged.
  Reviews.prototype.reorder = function (c) {
    var media = arr(c.querySelectorAll('.jdgm-rev__content > .jdgm-rev__pics, .jdgm-rev__content > .jdgm-rev__vids'));
    for (var k = media.length - 1; k >= 0; k--) c.insertBefore(media[k], c.firstChild);
    var row = c.querySelector('.jdgm-row-rating'), profile = c.querySelector('.jdgm-row-profile');
    if (row && profile && row.parentNode === profile.parentNode && follows(row, profile)) row.parentNode.insertBefore(profile, row);
    var stars = row && row.querySelector('.jdgm-rev__rating'), time = row && row.querySelector('.jdgm-rev__timestamp');
    if (stars && time && stars.parentNode === row && time.parentNode === row && follows(stars, time)) row.insertBefore(time, stars);
  };

  // "Item type:" + the product the review was written for (Judge.me's data-product-title), as real text after the
  // review text. It used to be CSS content (::before / ::after), which screen readers read apart (WCAG 1.3.1 / F87).
  Reviews.prototype.addItem = function (c) {
    var title = (c.getAttribute('data-product-title') || '').trim();
    if (!title || c.querySelector('.am7-reviews__item')) return;
    var p = document.createElement('p'), name = document.createElement('span');
    p.className = 'am7-reviews__item';
    if (this.itemLabel) {
      var label = document.createElement('span');
      label.className = 'am7-reviews__item-label';
      label.textContent = this.itemLabel;
      p.appendChild(label);
      p.appendChild(document.createTextNode(' '));
    }
    name.className = 'am7-reviews__item-name';
    name.textContent = title;
    p.appendChild(name);
    var box = c.querySelector('.jdgm-rev__content') || c;
    var ref = arr(box.children).filter(function (el) {
      return el.classList.contains('jdgm-rev__transparency-badge-wrapper') || el.classList.contains('jdgm-rev__reply');
    })[0] || null;
    box.insertBefore(p, ref);
  };

  // Judge.me names every photo link "<author> review photo 1", and many reviewers are "Anonymous", so different photos
  // share a name (WCAG 2.4.4, the store's F-22 issue type). Each link is named after its review instead, unique in the
  // list, and says that it opens a new tab.
  Reviews.prototype.namePhotos = function (c, i, names) {
    var links = arr(c.querySelectorAll('a.jdgm-rev__pic-link'));
    if (!links.length) return;
    var author = text(c.querySelector('.jdgm-rev__author')) || 'a customer';
    var date = text(c.querySelector('.jdgm-rev__timestamp'));
    links.forEach(function (a, k) {
      var name = 'Review photo' + (k ? ' ' + (k + 1) : '') + ' from ' + author + (date ? ', ' + date : '');
      if (names[name]) name += ', review ' + (i + 1);
      names[name] = true;
      if (a.getAttribute('target') === '_blank') name += ' (opens in a new tab)';
      if (a.getAttribute('aria-label') !== name) a.setAttribute('aria-label', name);
    });
  };

  /* ---------- masonry ---------- */
  Reviews.prototype.gap = function () {
    var g = parseFloat(getComputedStyle(this.root).getPropertyValue('--am7-reviews-gap'));
    return isNaN(g) ? 0 : g;
  };
  Reviews.prototype.span = function (el, gap) {
    if (!el.getClientRects().length) { el.style.gridRowEnd = ''; return; }
    if (gap === undefined) gap = this.gap();
    var h = el.getBoundingClientRect().height;
    el.style.gridRowEnd = 'span ' + Math.max(1, Math.ceil(h + gap));
  };
  Reviews.prototype.layout = function () {
    var list = this.list(), self = this;
    if (!list) return;
    var gap = this.gap();
    arr(list.children).forEach(function (el) {
      self.span(el, gap);
      if (self.ro && !el.__am7Ro) { el.__am7Ro = true; self.ro.observe(el); }
    });
    this.root.classList.add('am7-is-masonry');
  };

  /* ---------- photos ---------- */
  // Judge.me hides a photo (visibility: hidden) while its link has .jdgm--loading, and only clears that once its own lazy
  // loader has set src and seen the load. On our moved cards that loader doesn't always run, so a photo could load and
  // still stay hidden behind the grey placeholder (it opened fine on click). Each photo gets its src here (the browser's
  // loading="lazy" still defers it) and the flag is cleared as soon as the image has loaded.
  Reviews.prototype.upgradePhotos = function (cards) {
    cards.forEach(function (c) {
      arr(c.querySelectorAll('img.jdgm-rev__pic-img:not([data-am7-srcset])')).forEach(function (img) {
        img.setAttribute('data-am7-srcset', '');
        var src = img.getAttribute('data-src') || img.getAttribute('src') || '';
        var link = img.closest('.jdgm-rev__pic-link');
        img.loading = 'lazy';
        img.decoding = 'async';
        if (/^https:\/\/(review-images\.judgeme\.com|judgeme\.imgix\.net)\//.test(src) && /[?&]width=\d+/.test(src)) {
          img.addEventListener('error', function () { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }, { once: true });
          img.sizes = '(min-width: 1200px) 214px, (min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
          img.srcset = [320, 480, 640, 960].map(function (w) {
            return src.replace(/([?&]width=)\d+/, '$1' + w) + ' ' + w + 'w';
          }).join(', ');
        }
        if (src && !img.getAttribute('src')) img.setAttribute('src', src);
        function shown() { if (link) link.classList.remove('jdgm--loading'); }
        if (img.complete && img.naturalWidth) shown();
        else img.addEventListener('load', shown);
      });
    });
  };

  /* ---------- Judge.me's automatic titles repeat the first sentence of the review: hide those ---------- */
  Reviews.prototype.dedupeTitles = function (cards) {
    function norm(t) { return (t || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }
    cards.forEach(function (c) {
      var title = c.querySelector('.jdgm-rev__title');
      if (!title || title.hasAttribute('data-am7-checked')) return;
      var body = c.querySelector('.jdgm-rev__body');
      if (!body) return;
      title.setAttribute('data-am7-checked', '');
      var t = norm(title.textContent);
      if (t && norm(body.textContent).indexOf(t) === 0) title.classList.add('am7-reviews__dup');
    });
  };

  /* ---------- "Read more" on long reviews (CSS clamps the text to --am7-reviews-lines lines) ---------- */
  Reviews.prototype.readMore = function (cards) {
    var root = this.root;
    var more = root.getAttribute('data-am7-read-more') || 'Read more';
    var less = root.getAttribute('data-am7-read-less') || 'Read less';
    cards.forEach(function (c) {
      var body = c.querySelector('.jdgm-rev__body');
      if (!body || !c.getClientRects().length) return;
      var btn = c.querySelector('.am7-reviews__read');
      var open = body.classList.contains('am7-is-open');
      var cut = !open && body.scrollHeight > body.clientHeight + 2;
      if (!cut && !open) { if (btn) btn.parentNode.removeChild(btn); return; }
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'am7-reviews__read';
        if (!body.id) body.id = uid('am7-rev-body-');
        btn.setAttribute('aria-controls', body.id);
        btn.addEventListener('click', function () {
          var isOpen = body.classList.toggle('am7-is-open');
          btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          btn.textContent = isOpen ? less : more;
        });
        body.parentNode.insertBefore(btn, body.nextSibling);
      }
      // the buttons all say "Read more": the reviewer's name, as description, tells them apart ("Read more, Vicky L.")
      var author = c.querySelector('.jdgm-rev__author');
      if (author) {
        if (!author.id) author.id = uid('am7-rev-author-');
        if (btn.getAttribute('aria-describedby') !== author.id) btn.setAttribute('aria-describedby', author.id);
      }
      // only write when something changes: a text write is a DOM mutation, and mutations call sync() again
      var label = open ? less : more;
      if (btn.textContent !== label) btn.textContent = label;
      if (btn.getAttribute('aria-expanded') !== String(open)) btn.setAttribute('aria-expanded', String(open));
    });
  };

  /* ---------- SHOW MORE ---------- */
  Reviews.prototype.more = function () {
    if (this.loading && !this.loading.silent) return;
    var cards = this.cards(), limit = this.limit(), step = this.step();
    if (cards.length > limit) { this.reveal(limit, Math.min(cards.length, limit + step)); return; }
    if (this.loading) { this.loading.silent = false; this.sync(); return; } // a background fetch is running: wait for it
    this.load(false);
  };

  Reviews.prototype.reveal = function (from, to) {
    var hadFocus = !!this.btn && document.activeElement === this.btn;
    this.shown = to;
    this.revealing = true;
    this.sync();
    this.revealing = false;
    var first = to > from ? this.cards()[from] : null;
    if (first) {
      this.focusCard(first);
      this.say('Showing ' + to + ' reviews');
    } else if (hadFocus && this.btn.hidden) {
      this.rescue();
    } else {
      this.say(this.failed ? MSG_FAILED : MSG_ALL);
    }
  };

  // SHOW MORE disappears while it has keyboard focus (no further page, or Judge.me's page did not come): focus the last
  // review shown, so keyboard and screen-reader users keep their place, and say why (WCAG 2.4.3 / 4.1.3)
  Reviews.prototype.rescue = function () {
    var shown = this.cards().filter(function (c) { return !c.classList.contains('am7-reviews__off'); });
    this.focusCard(shown[shown.length - 1]);
    this.say(this.failed ? MSG_FAILED : MSG_ALL);
  };

  Reviews.prototype.focusCard = function (card) {
    if (!card) return;
    if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');
    try { card.focus({ preventScroll: true }); } catch (e) { /* old browsers */ }
    // never leave the focused card wholly off screen (WCAG 2.4.11)
    var r = card.getBoundingClientRect();
    if (r.bottom <= 0 || r.top >= window.innerHeight) card.scrollIntoView({ block: 'nearest' });
  };

  // polite status message; the same text twice is cleared first so it is announced again
  Reviews.prototype.say = function (msg) {
    var s = this.status;
    if (!s) return;
    if (s.textContent !== msg) { s.textContent = msg; return; }
    s.textContent = '';
    setTimeout(function () { s.textContent = msg; }, 100);
  };

  Reviews.prototype.load = function (silent) {
    var link = this.nextLink(), self = this;
    if (!link || this.loading) return;
    var cards = this.cards();
    this.loading = { keep: cards, before: cards.length, from: this.limit(), silent: silent };
    var trigger = document.createElement('a');
    trigger.className = 'jdgm-paginate__load-more am7-reviews__trigger';
    trigger.setAttribute('data-page', link.getAttribute('data-page'));
    trigger.hidden = true;
    link.parentNode.appendChild(trigger);
    this.loading.timer = setTimeout(function () { self.fail(); }, LOAD_TIMEOUT);
    if (this.btn && !silent) this.btn.setAttribute('aria-busy', 'true');
    trigger.click();
  };

  Reviews.prototype.fail = function () {
    var ld = this.loading;
    var hadFocus = !!this.btn && document.activeElement === this.btn;
    this.failed = true;
    this.loading = null;
    this.late = ld;          // Judge.me may still answer (e.g. after an HTTP 429): onMutate then merges that page
    this.sync();             // hides SHOW MORE; sync() moves focus off it first if it had focus
    if (ld && !ld.silent && !hadFocus) this.say(MSG_FAILED);
  };

  // focus was on a card node that Judge.me's page swap took out (and that is now back): focus it again
  Reviews.prototype.restoreFocus = function () {
    var f = this.focus, a = document.activeElement;
    if (!f || (a && a !== document.body) || f.at < 0 || (f.at && Date.now() - f.at > 300)) return;
    if (!this.widget.contains(f.el) || !f.el.getClientRects().length) return;
    try { f.el.focus({ preventScroll: true }); } catch (e) { /* old browsers */ }
  };

  Reviews.prototype.onMutate = function () {
    var ld = this.loading || this.late;
    if (ld) {
      var list = this.list();
      var fresh = this.cards().filter(function (c) { return ld.keep.indexOf(c) < 0; });
      if (list && fresh.length) {
        // Judge.me replaced the page: put the earlier cards back in front, drop repeats
        var anchor = list.firstChild, seen = {};
        ld.keep.forEach(function (k) {
          seen[reviewId(k)] = true;
          if (k.parentNode !== list) list.insertBefore(k, anchor);
        });
        fresh.forEach(function (f) { if (reviewId(f) && seen[reviewId(f)] && f.parentNode) f.parentNode.removeChild(f); });
        this.restoreFocus();
        clearTimeout(ld.timer);
        var late = ld === this.late;
        this.loading = null;
        this.late = null;
        var total = this.cards().length;
        if (late) this.failed = false;                          // it came after the time-out after all
        if (!ld.silent && total <= ld.before) this.done = true; // the page held nothing new: stop asking
        if (ld.silent || late) {
          this.sync();
          if (late && !ld.silent && total > ld.before) this.say(MSG_LATE);   // SHOW MORE is back
        } else {
          this.reveal(ld.from, Math.min(total, ld.from + this.step()));
        }
        return;
      }
    }
    // batch everything else (Judge.me's set-up, our own button) into one sync before the next paint
    var self = this;
    if (!this._raf) this._raf = requestAnimationFrame(function () { self._raf = 0; self.sync(); });
  };

  /* ---------- init ---------- */
  function init(scope) {
    arr((scope || document).querySelectorAll('[data-am7-reviews]')).forEach(function (root) {
      if (!root.__am7Reviews) root.__am7Reviews = new Reviews(root);
    });
  }
  AM7.reviews = { init: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
